import React from "react"
import { useTranslation } from "react-i18next"

import { homeCardStyle, homeColor, homeText } from "../home-theme.ts"
import { HomeSectionBand } from "./HomeSectionBand.tsx"
import { HomeLocationsSection } from "./locations/HomeLocationsSection"

/**
 * GC Default's page composition.
 *
 * This is the whole body — it REPLACES the legacy arrangement rather than
 * sitting above or below it. There is deliberately no page-level two-column
 * wrapper here: the old one declared a right rail for the whole page, so a
 * tall Next Best Actions column next to a short main column left a large
 * blank area under the main content. Each section now owns its own grid and
 * takes its natural height, which is what removes that gap without a fixed
 * height, a negative margin or absolute positioning.
 *
 * Every slot is a node the caller resolves, so this file decides ORDER and
 * ARRANGEMENT only and never reaches for data of its own.
 */
export interface HomeGcDefaultBodyProps {
  /** §4 — the four counts. */
  todayBand: React.ReactNode
  /** §4 — up to three priority cards. */
  priorityActions: React.ReactNode
  /** Sits in the Today heading, aligned right. */
  viewAllActions?: React.ReactNode
  /** Sits in the Locations heading, aligned right. */
  openLocations?: React.ReactNode

  /** §6 — Revenue / Outstanding receivables / Overdue invoices. */
  financialBand: React.ReactNode
  /**
   * The heading's right-hand line: "As of today · This month". Names which
   * figures are balances and which the selected window narrows, so a
   * period figure never reads as an as-of-today one.
   */
  financialCaption?: React.ReactNode
  cashFlow: React.ReactNode
  financialSnapshot: React.ReactNode

  /** §7 — the two schedule cards. */
  upcomingMilestones: React.ReactNode
  approvals: React.ReactNode

  /** §8 — the two pipeline cards, then the report links. */
  salesPipeline: React.ReactNode
  recentActivity: React.ReactNode
  favoriteReports?: React.ReactNode
}

/**
 * A card slot with nothing behind it yet.
 *
 * Shown instead of inventing rows. §7 is explicit that an unavailable source
 * must read as unavailable rather than be filled with plausible-looking
 * records, and a silently missing card would instead read as "this account
 * has none", which is a different and false claim.
 */
export const UnavailableCard = ({
  title,
  reason
}: {
  title: string
  reason: string
}) => (
  <section style={{ ...homeCardStyle, padding: 16, minWidth: 0 }}>
    <h3 style={{ ...homeText.itemTitle, color: homeColor.ink, margin: 0 }}>
      {title}
    </h3>
    <p
      style={{ ...homeText.meta, color: homeColor.inkMuted, margin: "6px 0 0" }}
    >
      {reason}
    </p>
  </section>
)

export const HomeGcDefaultBody = ({
  todayBand,
  priorityActions,
  viewAllActions,
  openLocations,
  financialBand,
  financialCaption,
  cashFlow,
  financialSnapshot,
  upcomingMilestones,
  approvals,
  salesPipeline,
  recentActivity,
  favoriteReports
}: HomeGcDefaultBodyProps) => {
  const { t } = useTranslation()

  return (
    <>
      <HomeSectionBand id="today" action={viewAllActions}>
        {todayBand}
        {priorityActions}
      </HomeSectionBand>

      <HomeSectionBand id="locations" action={openLocations}>
        <HomeLocationsSection />
      </HomeSectionBand>

      <HomeSectionBand id="financial" action={financialCaption}>
        {financialBand}
        <div className="home-section-pair">
          {cashFlow}
          {financialSnapshot}
        </div>
      </HomeSectionBand>

      <HomeSectionBand id="schedule">
        <div className="home-section-pair">
          {upcomingMilestones}
          {approvals}
        </div>
      </HomeSectionBand>

      <HomeSectionBand id="pipeline">
        <div className="home-section-pair">
          {salesPipeline}
          {recentActivity}
        </div>
        {favoriteReports ?? (
          <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
            {t("home.pipeline.noReports", "No favourite reports yet.")}
          </span>
        )}
      </HomeSectionBand>
    </>
  )
}
